/* Generated by restful-react */

import React from 'react';
import { Get, GetProps, useGet, UseGetProps, Mutate, MutateProps, useMutate, UseMutateProps } from 'restful-react';
export const SPEC_VERSION = '2.0.0';
/**
 * An API error response object. The errors array will contain object(s) with more information about exactly what went wrong with the request
 */
export interface APIError {
  /**
   * An application-specific error code
   */
  code: number;
  /**
   * A human-readable explanation specific to this occurrence of the problem
   */
  message?: string;
  correlationId?: string;
  /**
   * A short, human-readable summary of the problem
   */
  title: string;
}

/**
 * Address for a location
 */
export interface Address {
  /**
   * Address line 1
   */
  address1: string;
  /**
   * Address line 2
   */
  address2?: string;
  /**
   * Address line 3
   */
  address3?: string;
  /**
   * City or town
   */
  city: string;
  /**
   * Country
   */
  country: string;
  /**
   * Post code
   */
  postCode?: string;
  /**
   * State or province
   */
  state?: string;
}

export interface AnyObject {
  [key: string]: {};
}

/**
 * Claim a Evnex charge point
 */
export interface ChargePointClaim {
  /**
   * Configuration settings
   */
  configuration?: {
    /**
     * If the charge point has plug and charge enabled
     */
    plugAndCharge?: boolean;
  };
  electricityCost?: ElectricityCostCreate;
  /**
   * Location id to assign this charge point to
   */
  locationId: string;
  metadata?: ChargePointMetadata;
  /**
   * Display name for this charge point
   */
  name: string;
  /**
   * Organisation id to assign this charge point to
   */
  organisationId: string;
  /**
   * Serial number of the Evnex charge point being claimed
   */
  serial: string;
  /**
   * Default token to be set on charge point sessions
   */
  defaultToken?: string;
  /**
   * If a token is required to start charging
   */
  tokenRequired?: boolean;
  /**
   * Maximum charging current the charge point can draw
   */
  maxCurrent?: number;
}

/**
 * Charge point metadata
 */
export interface ChargePointMetadata {
  /**
   * reference id of charge point
   */
  referenceId?: string;
}

/**
 * Charging period profile used by load management and scheduling
 */
export interface ChargingProfilePeriod {
  /**
   * Charging limit of the profile
   */
  limit: number;
  /**
   * Time in seconds that this profile begins
   */
  start: number;
}

/**
 * A color string
 */
export type Color = string;

/**
 * Object used send command to change availability
 */
export interface CommandChangeAvailability {
  /**
   * Type of availability change that the Charge Point should perform.
   */
  changeAvailabilityType?: 'Inoperative' | 'Operative';
  connectorId: number;
}

/**
 * Object used to send the command to change configuration
 */
export interface CommandChangeConfiguration {
  /**
   * The name of the configuration setting to change
   */
  key: string;
  /**
   * The new value as string for the setting.
   */
  value: string;
}

/**
 * Object used send command remote start transaction
 */
export interface CommandRemoteStartTransaction {
  chargingProfile?: AnyObject;
  connectorId?: number;
  tokenUid: string;
}

/**
 * Object used send command remote stop transaction
 */
export interface CommandRemoteStopTransaction {
  connectorId: number;
}

/**
 * Object used send command reset
 */
export interface CommandReset {
  /**
   * Type of reset that the charge point should perform
   */
  resetType: 'Hard' | 'Soft';
}

/**
 * Command status response
 */
export type CommandResponseStatus = 'Accepted' | 'Rejected';

/**
 * Object used send a unlock connector command
 */
export interface CommandUnlockConnector {
  /**
   * Id of the connector to unlock
   */
  connectorId: number;
}

/**
 * Cost object used by electricity pricing
 */
export interface Cost {
  /**
   * Price of electricity
   */
  cost: number;
  /**
   * Time in seconds that this cost begins
   */
  start: number;
}

/**
 * An ISO 4217 currency code
 */
export type Currency = string;

/**
 * A duration in seconds. Either `86400` seconds (daily) or `604800` seconds (weekly)
 */
export type Duration = 86400 | 604800;

/**
 * Object used to create a new electricity cost
 */
export interface ElectricityCostCreate {
  /**
   * An array of cost objects that represent the changes in price over the duration
   */
  costs: Cost[];
  currency: Currency;
  duration: Duration;
}

/**
 * Electricity unit type
 */
export type ElectricityUnit = 'A' | 'W';

/**
 * Object used to send command to get a configured value
 */
export interface GetConfigurationKeyValue {
  /**
   * The name of the configuration setting
   */
  key: string;
  /**
   * Whether the value can be configured.
   */
  readonly: boolean;
  /**
   * The value of the configuration setting
   */
  value?: string;
}

/**
 * Load management profile for a charge point
 */
export interface LoadManagement {
  /**
   * An array of charging profile periods
   */
  chargingProfilePeriods?: ChargingProfilePeriod[];
  duration: Duration;
  /**
   * If the load management profile is enabled
   */
  enabled: boolean;
  /**
   * Time zone of the location where profile is applied
   */
  timezone?: string;
  units: ElectricityUnit;
}

/**
 * Create or update Load management profile for a charge point
 */
export interface LoadManagementUpdate {
  /**
   * An array of charging profile periods
   */
  chargingProfilePeriods: ChargingProfilePeriod[];
  duration: Duration;
  /**
   * If the load management profile is enabled
   */
  enabled: boolean;
  units: ElectricityUnit;
}

/**
 * Organisation summary object
 */
export interface OrganisationInfo {
  /**
   * Organisation's avatar url
   */
  avatar?: string;
  color?: Color;
  /**
   * Organisation's id
   */
  id: string;
  /**
   * Organisation's display name
   */
  name: string;
  slug: OrganisationSlug;
  tier: Tier;
}

/**
 * Unique URL for an organisation
 */
export type OrganisationSlug = string;

export interface ResponseApiErrors {
  errors: APIError[];
}

/**
 * Charge point get configuration response
 */
export interface ResponseCommandGetConfiguration {
  data: {
    items?: GetConfigurationKeyValue[];
  };
}

/**
 * Charge point command response
 */
export interface ResponsePostCommand {
  data: {
    message?: string;
    status: CommandResponseStatus;
  };
}

export interface ResponsePostEvnexChargePoint {
  data: {
    chargePointId: string;
  };
}

export interface ResponsePutLoadManagement {
  data: LoadManagement;
}

export interface ResponsePutUser {
  data: User;
}

/**
 * A role for a user in an organisation
 */
export type Role = 0 | 1 | 2;

/**
 * The tier of an organisation
 */
export type Tier = 1 | 2 | 3 | 4;

/**
 * A complete user object
 */
export type User = UserInfo & {
  /**
   * Date the user was created
   */
  createdDate: string;
  /**
   * List of organisations that the user belongs to
   */
  organisations: UserOrganisation[];
  /**
   * Date the user was last updated
   */
  updatedDate: string;
};

/**
 * User summary object
 */
export interface UserInfo {
  /**
   * User's avatar url
   */
  avatar?: string;
  color?: Color;
  /**
   * User's email address
   */
  email: string;
  /**
   * User's unique id
   */
  id: string;
  /**
   * User's display name
   */
  name?: string;
}

/**
 * A user's organisation. Contains the user's role within the organisation and if it is their default selected organisation
 */
export type UserOrganisation = OrganisationInfo & {
  isDefault: boolean;
  role: Role;
};

/**
 * User update object
 */
export interface UserUpdate {
  avatar?: string;
  color?: Color;
  defaultOrganisationId?: string;
  name?: string;
}

export type PostChargePointEvnexProps = Omit<
  MutateProps<ResponsePostEvnexChargePoint, ResponseApiErrors, void, ChargePointClaim, void>,
  'path' | 'verb'
>;

/**
 * Claim Evnex charge point
 *
 * Claim a Evnex charge point.
 */
export const PostChargePointEvnex = (props: PostChargePointEvnexProps) => (
  <Mutate<ResponsePostEvnexChargePoint, ResponseApiErrors, void, ChargePointClaim, void>
    verb="POST"
    path={`/v2/apps/charge-points/evnex`}
    {...props}
  />
);

export type UsePostChargePointEvnexProps = Omit<
  UseMutateProps<ResponsePostEvnexChargePoint, ResponseApiErrors, void, ChargePointClaim, void>,
  'path' | 'verb'
>;

/**
 * Claim Evnex charge point
 *
 * Claim a Evnex charge point.
 */
export const usePostChargePointEvnex = (props: UsePostChargePointEvnexProps) =>
  useMutate<ResponsePostEvnexChargePoint, ResponseApiErrors, void, ChargePointClaim, void>(
    'POST',
    `/v2/apps/charge-points/evnex`,
    props,
  );

export interface PutChargePointLoadManagementPathParams {
  /**
   * Id of a charge point
   */
  chargePointId: string;
}

export type PutChargePointLoadManagementProps = Omit<
  MutateProps<
    ResponsePutLoadManagement,
    ResponseApiErrors,
    void,
    LoadManagementUpdate,
    PutChargePointLoadManagementPathParams
  >,
  'path' | 'verb'
> &
  PutChargePointLoadManagementPathParams;

/**
 * Create or update a charge point's load managment profile.
 */
export const PutChargePointLoadManagement = ({ chargePointId, ...props }: PutChargePointLoadManagementProps) => (
  <Mutate<
    ResponsePutLoadManagement,
    ResponseApiErrors,
    void,
    LoadManagementUpdate,
    PutChargePointLoadManagementPathParams
  >
    verb="PUT"
    path={`/v2/apps/charge-points/${chargePointId}/load-management`}
    {...props}
  />
);

export type UsePutChargePointLoadManagementProps = Omit<
  UseMutateProps<
    ResponsePutLoadManagement,
    ResponseApiErrors,
    void,
    LoadManagementUpdate,
    PutChargePointLoadManagementPathParams
  >,
  'path' | 'verb'
> &
  PutChargePointLoadManagementPathParams;

/**
 * Create or update a charge point's load managment profile.
 */
export const usePutChargePointLoadManagement = ({ chargePointId, ...props }: UsePutChargePointLoadManagementProps) =>
  useMutate<
    ResponsePutLoadManagement,
    ResponseApiErrors,
    void,
    LoadManagementUpdate,
    PutChargePointLoadManagementPathParams
  >(
    'PUT',
    (paramsInPath: PutChargePointLoadManagementPathParams) =>
      `/v2/apps/charge-points/${paramsInPath.chargePointId}/load-management`,
    { pathParams: { chargePointId }, ...props },
  );

export type DeleteLocationProps = Omit<MutateProps<void, ResponseApiErrors, void, string, void>, 'path' | 'verb'>;

/**
 * Delete location
 *
 * Delete a location.
 */
export const DeleteLocation = (props: DeleteLocationProps) => (
  <Mutate<void, ResponseApiErrors, void, string, void> verb="DELETE" path={`/v2/apps/locations`} {...props} />
);

export type UseDeleteLocationProps = Omit<UseMutateProps<void, ResponseApiErrors, void, string, void>, 'path' | 'verb'>;

/**
 * Delete location
 *
 * Delete a location.
 */
export const useDeleteLocation = (props: UseDeleteLocationProps) =>
  useMutate<void, ResponseApiErrors, void, string, void>('DELETE', `/v2/apps/locations`, { ...props });

export interface PostChargePointCommandChangeAvailabilityPathParams {
  /**
   * Slug of a organisation
   */
  organisationSlug: OrganisationSlug;
  /**
   * Id of a charge point
   */
  chargePointId: string;
}

export type PostChargePointCommandChangeAvailabilityProps = Omit<
  MutateProps<
    ResponsePostCommand,
    ResponseApiErrors,
    void,
    CommandChangeAvailability,
    PostChargePointCommandChangeAvailabilityPathParams
  >,
  'path' | 'verb'
> &
  PostChargePointCommandChangeAvailabilityPathParams;

/**
 * Send a change availability command to the charge point
 *
 * Send a command to change the availability of a charge point.
 */
export const PostChargePointCommandChangeAvailability = ({
  organisationSlug,
  chargePointId,
  ...props
}: PostChargePointCommandChangeAvailabilityProps) => (
  <Mutate<
    ResponsePostCommand,
    ResponseApiErrors,
    void,
    CommandChangeAvailability,
    PostChargePointCommandChangeAvailabilityPathParams
  >
    verb="POST"
    path={`/v2/apps/organisations/${organisationSlug}/charge-points/${chargePointId}/commands/change-availability`}
    {...props}
  />
);

export type UsePostChargePointCommandChangeAvailabilityProps = Omit<
  UseMutateProps<
    ResponsePostCommand,
    ResponseApiErrors,
    void,
    CommandChangeAvailability,
    PostChargePointCommandChangeAvailabilityPathParams
  >,
  'path' | 'verb'
> &
  PostChargePointCommandChangeAvailabilityPathParams;

/**
 * Send a change availability command to the charge point
 *
 * Send a command to change the availability of a charge point.
 */
export const usePostChargePointCommandChangeAvailability = ({
  organisationSlug,
  chargePointId,
  ...props
}: UsePostChargePointCommandChangeAvailabilityProps) =>
  useMutate<
    ResponsePostCommand,
    ResponseApiErrors,
    void,
    CommandChangeAvailability,
    PostChargePointCommandChangeAvailabilityPathParams
  >(
    'POST',
    (paramsInPath: PostChargePointCommandChangeAvailabilityPathParams) =>
      `/v2/apps/organisations/${paramsInPath.organisationSlug}/charge-points/${paramsInPath.chargePointId}/commands/change-availability`,
    { pathParams: { organisationSlug, chargePointId }, ...props },
  );

export interface PostChargePointCommandStartTransactionPathParams {
  /**
   * Slug of a organisation
   */
  organisationSlug: OrganisationSlug;
  /**
   * Id of a charge point
   */
  chargePointId: string;
}

export type PostChargePointCommandStartTransactionProps = Omit<
  MutateProps<
    ResponsePostCommand,
    ResponseApiErrors,
    void,
    CommandRemoteStartTransaction,
    PostChargePointCommandStartTransactionPathParams
  >,
  'path' | 'verb'
> &
  PostChargePointCommandStartTransactionPathParams;

/**
 * Send a remote start transaction command to the charge point
 *
 * Send a command to start a transaction on a connector of a charge point.
 */
export const PostChargePointCommandStartTransaction = ({
  organisationSlug,
  chargePointId,
  ...props
}: PostChargePointCommandStartTransactionProps) => (
  <Mutate<
    ResponsePostCommand,
    ResponseApiErrors,
    void,
    CommandRemoteStartTransaction,
    PostChargePointCommandStartTransactionPathParams
  >
    verb="POST"
    path={`/v2/apps/organisations/${organisationSlug}/charge-points/${chargePointId}/commands/remote-start-transaction`}
    {...props}
  />
);

export type UsePostChargePointCommandStartTransactionProps = Omit<
  UseMutateProps<
    ResponsePostCommand,
    ResponseApiErrors,
    void,
    CommandRemoteStartTransaction,
    PostChargePointCommandStartTransactionPathParams
  >,
  'path' | 'verb'
> &
  PostChargePointCommandStartTransactionPathParams;

/**
 * Send a remote start transaction command to the charge point
 *
 * Send a command to start a transaction on a connector of a charge point.
 */
export const usePostChargePointCommandStartTransaction = ({
  organisationSlug,
  chargePointId,
  ...props
}: UsePostChargePointCommandStartTransactionProps) =>
  useMutate<
    ResponsePostCommand,
    ResponseApiErrors,
    void,
    CommandRemoteStartTransaction,
    PostChargePointCommandStartTransactionPathParams
  >(
    'POST',
    (paramsInPath: PostChargePointCommandStartTransactionPathParams) =>
      `/v2/apps/organisations/${paramsInPath.organisationSlug}/charge-points/${paramsInPath.chargePointId}/commands/remote-start-transaction`,
    { pathParams: { organisationSlug, chargePointId }, ...props },
  );

export interface PostChargePointCommandStopTransactionPathParams {
  /**
   * Slug of a organisation
   */
  organisationSlug: OrganisationSlug;
  /**
   * Id of a charge point
   */
  chargePointId: string;
}

export type PostChargePointCommandStopTransactionProps = Omit<
  MutateProps<
    ResponsePostCommand,
    ResponseApiErrors,
    void,
    CommandRemoteStopTransaction,
    PostChargePointCommandStopTransactionPathParams
  >,
  'path' | 'verb'
> &
  PostChargePointCommandStopTransactionPathParams;

/**
 * Send a remote stop transaction command to the charge point
 *
 * Send a command to stop a transaction on a connector of a charge point.
 */
export const PostChargePointCommandStopTransaction = ({
  organisationSlug,
  chargePointId,
  ...props
}: PostChargePointCommandStopTransactionProps) => (
  <Mutate<
    ResponsePostCommand,
    ResponseApiErrors,
    void,
    CommandRemoteStopTransaction,
    PostChargePointCommandStopTransactionPathParams
  >
    verb="POST"
    path={`/v2/apps/organisations/${organisationSlug}/charge-points/${chargePointId}/commands/remote-stop-transaction`}
    {...props}
  />
);

export type UsePostChargePointCommandStopTransactionProps = Omit<
  UseMutateProps<
    ResponsePostCommand,
    ResponseApiErrors,
    void,
    CommandRemoteStopTransaction,
    PostChargePointCommandStopTransactionPathParams
  >,
  'path' | 'verb'
> &
  PostChargePointCommandStopTransactionPathParams;

/**
 * Send a remote stop transaction command to the charge point
 *
 * Send a command to stop a transaction on a connector of a charge point.
 */
export const usePostChargePointCommandStopTransaction = ({
  organisationSlug,
  chargePointId,
  ...props
}: UsePostChargePointCommandStopTransactionProps) =>
  useMutate<
    ResponsePostCommand,
    ResponseApiErrors,
    void,
    CommandRemoteStopTransaction,
    PostChargePointCommandStopTransactionPathParams
  >(
    'POST',
    (paramsInPath: PostChargePointCommandStopTransactionPathParams) =>
      `/v2/apps/organisations/${paramsInPath.organisationSlug}/charge-points/${paramsInPath.chargePointId}/commands/remote-stop-transaction`,
    { pathParams: { organisationSlug, chargePointId }, ...props },
  );

export interface PostChargePointCommandResetPathParams {
  /**
   * Slug of a organisation
   */
  organisationSlug: OrganisationSlug;
  /**
   * Id of a charge point
   */
  chargePointId: string;
}

export type PostChargePointCommandResetProps = Omit<
  MutateProps<ResponsePostCommand, ResponseApiErrors, void, CommandReset, PostChargePointCommandResetPathParams>,
  'path' | 'verb'
> &
  PostChargePointCommandResetPathParams;

/**
 * Send a reset command to the charge point
 *
 * Send a command to reset a charge point.
 */
export const PostChargePointCommandReset = ({
  organisationSlug,
  chargePointId,
  ...props
}: PostChargePointCommandResetProps) => (
  <Mutate<ResponsePostCommand, ResponseApiErrors, void, CommandReset, PostChargePointCommandResetPathParams>
    verb="POST"
    path={`/v2/apps/organisations/${organisationSlug}/charge-points/${chargePointId}/commands/reset`}
    {...props}
  />
);

export type UsePostChargePointCommandResetProps = Omit<
  UseMutateProps<ResponsePostCommand, ResponseApiErrors, void, CommandReset, PostChargePointCommandResetPathParams>,
  'path' | 'verb'
> &
  PostChargePointCommandResetPathParams;

/**
 * Send a reset command to the charge point
 *
 * Send a command to reset a charge point.
 */
export const usePostChargePointCommandReset = ({
  organisationSlug,
  chargePointId,
  ...props
}: UsePostChargePointCommandResetProps) =>
  useMutate<ResponsePostCommand, ResponseApiErrors, void, CommandReset, PostChargePointCommandResetPathParams>(
    'POST',
    (paramsInPath: PostChargePointCommandResetPathParams) =>
      `/v2/apps/organisations/${paramsInPath.organisationSlug}/charge-points/${paramsInPath.chargePointId}/commands/reset`,
    { pathParams: { organisationSlug, chargePointId }, ...props },
  );

export interface PostChargePointCommandUnlockConnectorPathParams {
  /**
   * Slug of a organisation
   */
  organisationSlug: OrganisationSlug;
  /**
   * Id of a charge point
   */
  chargePointId: string;
}

export type PostChargePointCommandUnlockConnectorProps = Omit<
  MutateProps<
    ResponsePostCommand,
    ResponseApiErrors,
    void,
    CommandUnlockConnector,
    PostChargePointCommandUnlockConnectorPathParams
  >,
  'path' | 'verb'
> &
  PostChargePointCommandUnlockConnectorPathParams;

/**
 * Send a unlock connector command to the charge point
 *
 * Send a command to unlock a connector on a charge point.
 */
export const PostChargePointCommandUnlockConnector = ({
  organisationSlug,
  chargePointId,
  ...props
}: PostChargePointCommandUnlockConnectorProps) => (
  <Mutate<
    ResponsePostCommand,
    ResponseApiErrors,
    void,
    CommandUnlockConnector,
    PostChargePointCommandUnlockConnectorPathParams
  >
    verb="POST"
    path={`/v2/apps/organisations/${organisationSlug}/charge-points/${chargePointId}/commands/unlock-connector`}
    {...props}
  />
);

export type UsePostChargePointCommandUnlockConnectorProps = Omit<
  UseMutateProps<
    ResponsePostCommand,
    ResponseApiErrors,
    void,
    CommandUnlockConnector,
    PostChargePointCommandUnlockConnectorPathParams
  >,
  'path' | 'verb'
> &
  PostChargePointCommandUnlockConnectorPathParams;

/**
 * Send a unlock connector command to the charge point
 *
 * Send a command to unlock a connector on a charge point.
 */
export const usePostChargePointCommandUnlockConnector = ({
  organisationSlug,
  chargePointId,
  ...props
}: UsePostChargePointCommandUnlockConnectorProps) =>
  useMutate<
    ResponsePostCommand,
    ResponseApiErrors,
    void,
    CommandUnlockConnector,
    PostChargePointCommandUnlockConnectorPathParams
  >(
    'POST',
    (paramsInPath: PostChargePointCommandUnlockConnectorPathParams) =>
      `/v2/apps/organisations/${paramsInPath.organisationSlug}/charge-points/${paramsInPath.chargePointId}/commands/unlock-connector`,
    { pathParams: { organisationSlug, chargePointId }, ...props },
  );

export interface GetChargePointCommandGetConfigurationPathParams {
  /**
   * Slug of a organisation
   */
  organisationSlug: OrganisationSlug;
  /**
   * Id of a charge point
   */
  chargePointId: string;
}

export type GetChargePointCommandGetConfigurationProps = Omit<
  GetProps<ResponseCommandGetConfiguration, ResponseApiErrors, void, GetChargePointCommandGetConfigurationPathParams>,
  'path'
> &
  GetChargePointCommandGetConfigurationPathParams;

/**
 * Send a get configuration command to the charge point
 *
 * Send a command to get configuration of a charge point.
 */
export const GetChargePointCommandGetConfiguration = ({
  organisationSlug,
  chargePointId,
  ...props
}: GetChargePointCommandGetConfigurationProps) => (
  <Get<ResponseCommandGetConfiguration, ResponseApiErrors, void, GetChargePointCommandGetConfigurationPathParams>
    path={`/v2/apps/organisations/${organisationSlug}/charge-points/${chargePointId}/commands/get-configuration`}
    {...props}
  />
);

export type UseGetChargePointCommandGetConfigurationProps = Omit<
  UseGetProps<
    ResponseCommandGetConfiguration,
    ResponseApiErrors,
    void,
    GetChargePointCommandGetConfigurationPathParams
  >,
  'path'
> &
  GetChargePointCommandGetConfigurationPathParams;

/**
 * Send a get configuration command to the charge point
 *
 * Send a command to get configuration of a charge point.
 */
export const useGetChargePointCommandGetConfiguration = ({
  organisationSlug,
  chargePointId,
  ...props
}: UseGetChargePointCommandGetConfigurationProps) =>
  useGet<ResponseCommandGetConfiguration, ResponseApiErrors, void, GetChargePointCommandGetConfigurationPathParams>(
    (paramsInPath: GetChargePointCommandGetConfigurationPathParams) =>
      `/v2/apps/organisations/${paramsInPath.organisationSlug}/charge-points/${paramsInPath.chargePointId}/commands/get-configuration`,
    { pathParams: { organisationSlug, chargePointId }, ...props },
  );

export interface PostChargePointCommandChangeConfigurationPathParams {
  /**
   * Slug of a organisation
   */
  organisationSlug: OrganisationSlug;
  /**
   * Id of a charge point
   */
  chargePointId: string;
}

export type PostChargePointCommandChangeConfigurationProps = Omit<
  MutateProps<
    ResponsePostCommand,
    ResponseApiErrors,
    void,
    CommandChangeConfiguration,
    PostChargePointCommandChangeConfigurationPathParams
  >,
  'path' | 'verb'
> &
  PostChargePointCommandChangeConfigurationPathParams;

/**
 * Send a change configuration command to the charge point
 *
 * Send a change configuration to a charge point.
 */
export const PostChargePointCommandChangeConfiguration = ({
  organisationSlug,
  chargePointId,
  ...props
}: PostChargePointCommandChangeConfigurationProps) => (
  <Mutate<
    ResponsePostCommand,
    ResponseApiErrors,
    void,
    CommandChangeConfiguration,
    PostChargePointCommandChangeConfigurationPathParams
  >
    verb="POST"
    path={`/v2/apps/organisations/${organisationSlug}/charge-points/${chargePointId}/commands/change-configuration`}
    {...props}
  />
);

export type UsePostChargePointCommandChangeConfigurationProps = Omit<
  UseMutateProps<
    ResponsePostCommand,
    ResponseApiErrors,
    void,
    CommandChangeConfiguration,
    PostChargePointCommandChangeConfigurationPathParams
  >,
  'path' | 'verb'
> &
  PostChargePointCommandChangeConfigurationPathParams;

/**
 * Send a change configuration command to the charge point
 *
 * Send a change configuration to a charge point.
 */
export const usePostChargePointCommandChangeConfiguration = ({
  organisationSlug,
  chargePointId,
  ...props
}: UsePostChargePointCommandChangeConfigurationProps) =>
  useMutate<
    ResponsePostCommand,
    ResponseApiErrors,
    void,
    CommandChangeConfiguration,
    PostChargePointCommandChangeConfigurationPathParams
  >(
    'POST',
    (paramsInPath: PostChargePointCommandChangeConfigurationPathParams) =>
      `/v2/apps/organisations/${paramsInPath.organisationSlug}/charge-points/${paramsInPath.chargePointId}/commands/change-configuration`,
    { pathParams: { organisationSlug, chargePointId }, ...props },
  );

export type PutUserProps = Omit<
  MutateProps<ResponsePutUser, ResponseApiErrors, void, UserUpdate, void>,
  'path' | 'verb'
>;

/**
 * Update user
 *
 * Update the current user's details. The current user is determined by the `sub` in the JWT.
 */
export const PutUser = (props: PutUserProps) => (
  <Mutate<ResponsePutUser, ResponseApiErrors, void, UserUpdate, void> verb="PUT" path={`/v2/apps/user`} {...props} />
);

export type UsePutUserProps = Omit<
  UseMutateProps<ResponsePutUser, ResponseApiErrors, void, UserUpdate, void>,
  'path' | 'verb'
>;

/**
 * Update user
 *
 * Update the current user's details. The current user is determined by the `sub` in the JWT.
 */
export const usePutUser = (props: UsePutUserProps) =>
  useMutate<ResponsePutUser, ResponseApiErrors, void, UserUpdate, void>('PUT', `/v2/apps/user`, props);
